import { gridWithInputStyles } from 'components/technical/grids/gridStyles';
import GAgGrid from 'components/technical/grids/GAgGrid';
import { useTheme } from '@mui/joy';
import {
  amountColumn,
  NAV_PERC_MIN_DECIMAL_THRESHOLD,
  navPercentageColumn,
  priceColumn,
  sideWithBadgeColumnBasedOnSlotAndAmount,
  unitCostBasisColumn,
  unrealizedPnlWithColorsColumn,
  assetWithSymbolColumn,
  notionalColumn,
  navColumn,
} from 'components/technical/grids/SharedReportColumns';
import { calculateSubAccountToSubFundsMapper } from './PositionsSummary.util';
import { groupCostBasisByAssetAndSubFund } from '../account/SubAccountPositionsService';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { ISlotType, type IPortfolioDashboardInputQuery } from 'generated/graphql';
import { bignumber } from 'mathjs';
import { VenueIconList } from 'components/venue/VenueIconList';
import { PriceSummaryType, usePriceChanges } from './UsePriceChanges';
import { useFinalColorScheme } from 'technical/useFinalColorScheme';

interface VisualPosition {
  asset: IPortfolioDashboardInputQuery['portfolio']['positions']['positions'][number]['asset'];
  spot?: Omit<
    NonNullable<IPortfolioDashboardInputQuery['portfolio']['positions']['positions'][number]['spot']>,
    'slot'
  >;
  derivative?: IPortfolioDashboardInputQuery['portfolio']['positions']['positions'][number]['derivative'];
  subAccount: {
    name: string;
    venue: {
      label: string;
      id: string;
    };
  }[];
}

const PositionSummaryLight = ({
  portfolio,
  fullHeight,
}: {
  portfolio: IPortfolioDashboardInputQuery['portfolio'];
  fullHeight?: boolean;
}) => {
  const scheme = useFinalColorScheme();
  const costBasisPerAssetAndSubFund = groupCostBasisByAssetAndSubFund(portfolio.journal.costBasis);
  const costBasisDimension = portfolio.journal.preferences?.dimension ?? null;
  const subAccountToCostBasisSubFundsMapper = calculateSubAccountToSubFundsMapper(
    costBasisDimension,
    portfolio.subFunds.list
  );

  const totalNAV = portfolio.positions.positions.reduce((acc, pos) => {
    let partialAcc = acc;
    if (pos.spot) {
      partialAcc = partialAcc.plus(pos.spot.balance);
    } else if (pos.derivative) {
      partialAcc = partialAcc.plus(pos.derivative.balance || 0);
    }
    return partialAcc;
  }, bignumber(0));

  const spotsList = portfolio.positions.positions.filter((pos) => pos.spot);
  const spotAssetIds = new Map<string, { name: string; venue: { label: string; id: string } }[]>();

  for (const pos of spotsList) {
    if (!pos.spot) {
      continue;
    }

    if (spotAssetIds.has(pos.asset.id)) {
      const subAccounts = spotAssetIds.get(pos.asset.id);
      if (subAccounts) {
        const evaluatedAccount = {
          name: pos.subAccount.name,
          venue: {
            label: pos.subAccount.account.venue.label,
            id: pos.subAccount.account.id,
          },
        };
        subAccounts.push(evaluatedAccount);
        spotAssetIds.set(pos.asset.id, subAccounts);
      }
    } else {
      const evaluatedAccount = {
        name: pos.subAccount.name,
        venue: {
          label: pos.subAccount.account.venue.label,
          id: pos.subAccount.account.id,
        },
      };
      spotAssetIds.set(pos.asset.id, [evaluatedAccount]);
    }
  }

  const groupedSpots = spotsList.reduce((acc: Map<string, VisualPosition>, pos) => {
    const existing = acc.get(pos.asset.id);
    let amount = bignumber(pos.spot?.amount || 0);
    if (pos.spot?.slot === ISlotType.Borrowed) {
      amount = amount.neg();
    }

    if (existing) {
      const evaluated = {
        asset: existing.asset,
        spot: {
          price: bignumber(existing.spot?.price || 0).toString(),
          amount: bignumber(existing.spot?.amount || 0)
            .plus(amount)
            .toString(),
          balance: bignumber(existing.spot?.balance || 0)
            .plus(pos.spot?.balance || 0)
            .toString(),
        },
        subAccount: spotAssetIds.get(pos.asset.id) ?? [],
      };
      // biome-ignore lint/suspicious/noExplicitAny:
      acc.set(pos.asset.id, evaluated as any);
    } else {
      const evaluated = {
        asset: pos.asset,
        spot: {
          price: bignumber(pos.spot?.price || 0).toString(),
          amount: amount.toString(),
          balance: bignumber(pos.spot?.balance || 0).toString(),
        },
        subAccount: spotAssetIds.get(pos.asset.id) ?? [],
      };
      // biome-ignore lint/suspicious/noExplicitAny:
      acc.set(pos.asset.id, evaluated as any);
    }
    return acc;
  }, new Map<string, VisualPosition>());

  const derivativesList = portfolio.positions.positions.filter((pos) => pos.derivative);
  const derivativeAssetIds = new Map<string, { name: string; venue: { label: string; id: string } }[]>();

  for (const pos of derivativesList) {
    if (pos.derivative) {
      if (derivativeAssetIds.has(pos.asset.id)) {
        const subAccounts = derivativeAssetIds.get(pos.asset.id);
        if (subAccounts) {
          const evaluatedAccount = {
            name: pos.subAccount.name,
            venue: {
              label: pos.subAccount.account.venue.label,
              id: pos.subAccount.account.id,
            },
          };
          subAccounts.push(evaluatedAccount);
          derivativeAssetIds.set(pos.asset.id, subAccounts);
        }
      } else {
        const evaluatedAccount = {
          name: pos.subAccount.name,
          venue: {
            label: pos.subAccount.account.venue.label,
            id: pos.subAccount.account.id,
          },
        };
        derivativeAssetIds.set(pos.asset.id, [evaluatedAccount]);
      }
    }
  }

  const groupedDerivatives = derivativesList.reduce((acc: Map<string, VisualPosition>, pos) => {
    const existing = acc.get(pos.asset.id);
    if (existing) {
      const evaluated = {
        asset: existing.asset,
        derivative: existing.derivative,
        subAccount: derivativeAssetIds.get(pos.asset.id) ?? [],
      };
      acc.set(pos.asset.id, evaluated);
    } else {
      const evaluated = {
        asset: pos.asset,
        derivative: pos.derivative,
        subAccount: derivativeAssetIds.get(pos.asset.id) ?? [],
      };
      acc.set(pos.asset.id, evaluated);
    }
    return acc;
  }, new Map<string, VisualPosition>());

  const theme = useTheme();

  const arraySpots: VisualPosition[] = Array.from(groupedSpots.values());
  const arrayDerivatives: VisualPosition[] = Array.from(groupedDerivatives.values());
  const arrayPositions = [...arraySpots, ...arrayDerivatives];
  const assetIds = arrayPositions.map((pos) => pos.asset.id);
  const pricesChangesResult = usePriceChanges(assetIds);

  if (!pricesChangesResult.loaded) {
    return <pricesChangesResult.Fallback />;
  }

  const navValues = arrayPositions
    .map((pos) => {
      if (!pos) return 0;

      let value: number;
      if (pos.spot) {
        value = bignumber(pos.spot.balance).div(totalNAV).toNumber();
      } else if (pos.derivative) {
        value = bignumber(pos.derivative.balance || 0)
          .div(totalNAV)
          .toNumber();
      } else {
        return 0;
      }

      return Math.abs(value) < NAV_PERC_MIN_DECIMAL_THRESHOLD ? 0 : value;
    })
    .filter((val: number) => !Number.isNaN(val));

  const minNavValue = Math.min(...navValues, 0);
  const maxNavValue = Math.max(...navValues, 1);

  const columns: ColDef[] = [
    assetWithSymbolColumn({
      pinned: 'left',
      suppressHeaderMenuButton: true,
    }),
    {
      colId: 'account',
      suppressHeaderMenuButton: true,
      headerName: 'Account',
      type: 'textColumn',
      cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: {
        innerRenderer: (params: ICellRendererParams) => {
          const accounts = params.data?.subAccount.map(
            (account: { name: string; venue: { label: string; id: string } }) => ({
              name: account.name,
              venue: { label: account.venue.label, id: account.venue.id },
            })
          );
          return <VenueIconList accounts={accounts} maxItems={3} />;
        },
      },
      filter: false,
      sortable: false,
    },
    navColumn({
      suppressHeaderMenuButton: true,
    }),
    notionalColumn({
      suppressHeaderMenuButton: true,
      sideAware: true,
    }),
    navPercentageColumn({
      scheme,
      showBar: true,
      totalNAV: () => totalNAV.toNumber(),
      theme,
      minNavValue,
      maxNavValue,
    }),
    amountColumn({ initialHide: false, suppressHeaderMenuButton: true }),
    unitCostBasisColumn(costBasisPerAssetAndSubFund, subAccountToCostBasisSubFundsMapper, {
      suppressHeaderMenuButton: true,
    }),
    // TODO: add back the price column once we have the price changes for the last 24h
    // priceWithLast24hPriceChangeColumn(
    //   pricesChangesResult.priceSummary,
    //   PriceSummaryType.latestToday,
    //   'Latest',
    //   pricesChangesResult.priceChanges24hByAssetId,
    //   { suppressHeaderMenuButton: true }
    // ),
    priceColumn(pricesChangesResult.priceSummary, PriceSummaryType.latestToday, 'Latest', {
      suppressHeaderMenuButton: true,
    }),
    unrealizedPnlWithColorsColumn(costBasisPerAssetAndSubFund, subAccountToCostBasisSubFundsMapper, {
      suppressHeaderMenuButton: true,
    }),
    sideWithBadgeColumnBasedOnSlotAndAmount({ suppressHeaderMenuButton: true }),
  ];

  return (
    <GAgGrid
      height={fullHeight ? '100%' : '500px'}
      rowData={arrayPositions}
      columnDefs={columns}
      cellSelection={true}
      autoSizeStrategy={{ type: 'fitCellContents' }}
      defaultColDef={{
        resizable: true,
        sortable: true,
        filter: false,
        pivot: true,
      }}
      rowHeight={gridWithInputStyles.rowHeight}
    />
  );
};

export default PositionSummaryLight;
